import { Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';

import {
  cancelAiGeneration,
  getQuestionnaireScore,
  submitQuestionnaire,
} from '@/apis';
import { useApiHandler, useApiRequest } from '@/hooks';

import * as Style from './GeneratingResultView.style';

const TOTAL_STEPS = 21; // 17 個 SDG 描述 + 3 個個別回饋 + 1 個綜合回饋
const POLL_INTERVAL = 2000; // 每 2 秒檢查一次進度

interface GeneratingResultViewProps {
  questionnaireId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export default function GeneratingResultView({
  questionnaireId,
  onComplete,
  onCancel,
}: GeneratingResultViewProps) {
  const { handleApiCall } = useApiHandler();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [statusText, setStatusText] = useState('正在提交問卷...');
  const [isSubmitting, setIsSubmitting] = useState(true);
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const hasStartedGenerationRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isNavigatingRef = useRef(false);

  // Use useApiRequest for polling with auto-cancellation
  const { execute: checkProgress, cancel: cancelProgressCheck } = useApiRequest(
    () => getQuestionnaireScore(questionnaireId),
    {
      requestKey: `questionnaire-score-${questionnaireId}`,
      onSuccess: (result) => {
        // Use backend generation progress if available
        if (result.data.generationProgress) {
          const { completedSteps, currentStep, percentage } = result.data.generationProgress;

          setCurrentStep(completedSteps);
          setProgress(percentage);
          setStatusText(currentStep);

          // Check if feedbacks are ready (all generation complete)
          if (result.data.feedbacksReady && hasStartedGenerationRef.current) {
            console.log('All complete! Calling onComplete...');
            setProgress(100);
            setStatusText('分析報告生成完成！');
            setIsGenerationComplete(true);

            // Clear polling interval
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            // Wait a moment to show completion, then call onComplete
            setTimeout(() => {
              onComplete();
            }, 500);
          }
        } else {
          // Fallback to old logic if generationProgress is not available
          const completedCount = result.data.sdgScores.filter(
            (score) => score.aiDescription && score.aiDescription.trim() !== '',
          ).length;

          setCurrentStep(completedCount);
          const newProgress = (completedCount / TOTAL_STEPS) * 100;
          setProgress(newProgress);

          if (completedCount > 0) {
            setStatusText(`正在生成 SDG ${completedCount} 分析報告...`);
          }
        }
      },
      onError: (error) => {
        console.error('Failed to check progress:', error);
        // Continue polling even if there's an error
      },
    },
  );

  useEffect(() => {
    let isMounted = true;

    const startGeneration = async () => {
      try {
        // Submit questionnaire (this will trigger AI generation in background)
        await handleApiCall(() => submitQuestionnaire(questionnaireId), {
          showLoading: false,
        });

        if (!isMounted) return;

        setIsSubmitting(false);
        setStatusText('正在準備生成 AI 分析報告...');

        // Reset progress immediately after submission
        setProgress(0);
        setCurrentStep(0);

        // Wait a moment for backend to clear old descriptions
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!isMounted) return;

        hasStartedGenerationRef.current = true; // Mark that generation has started
        setStatusText('正在生成 SDG 分析報告...');

        // Start polling for progress using useApiRequest
        pollIntervalRef.current = setInterval(() => {
          checkProgress(); // This will auto-cancel previous request if still pending
        }, POLL_INTERVAL);

        // Check progress immediately
        checkProgress();
      } catch (error) {
        console.error('Failed to submit questionnaire:', error);
        if (!isMounted) return;

        // Even if submission failed, try to check if result exists
        setIsSubmitting(false);
        hasStartedGenerationRef.current = true; // Mark that we've started checking
        pollIntervalRef.current = setInterval(() => {
          checkProgress();
        }, POLL_INTERVAL);
        checkProgress();
      }
    };

    startGeneration();

    // Cleanup function - useApiRequest will auto-cancel the request
    return () => {
      isMounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [questionnaireId]);

  // Cleanup function for canceling generation
  const cleanupGeneration = useCallback(async () => {
    // Cancel ongoing API request (polling)
    cancelProgressCheck('用戶取消生成');

    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Call backend API to cancel AI generation
    try {
      console.log('取消後端 AI 生成作業');
      await cancelAiGeneration(questionnaireId, false);
    } catch (error) {
      console.error('取消 AI 生成失敗:', error);
    }
  }, [cancelProgressCheck, questionnaireId]);

  // Warn user when trying to leave/refresh the page (browser-level)
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!isGenerationComplete && hasStartedGenerationRef.current) {
          event.preventDefault();
          // Modern browsers ignore custom messages, but we still need to return a value
          return '報告生成尚未完成，離開後將取消生成。您確定要離開此頁面嗎？';
        }
      },
      [isGenerationComplete],
    ),
  );

  // Intercept browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Only block if generation is in progress and not already navigating
      if (
        !isGenerationComplete &&
        hasStartedGenerationRef.current &&
        !isNavigatingRef.current
      ) {
        // Prevent navigation by pushing current state back
        window.history.pushState(null, '', window.location.pathname);

        // Show confirmation modal
        Modal.confirm({
          title: '確定要離開嗎？',
          content: '報告生成尚未完成，離開後將取消生成。您確定要離開此頁面嗎？',
          okText: '確定離開',
          cancelText: '繼續等待',
          okButtonProps: { danger: true },
          onOk: async () => {
            // Mark that we're navigating to prevent re-triggering
            isNavigatingRef.current = true;

            // Cleanup generation - cancel API requests and polling
            await cleanupGeneration();

            // Remove the event listener before calling onCancel
            window.removeEventListener('popstate', handlePopState);

            // Call onCancel to let parent handle navigation
            onCancel();
          },
          onCancel: () => {
            // User chose to stay, do nothing
            console.log('用戶選擇繼續等待');
          },
        });
      }
    };

    // Push initial state to enable detection
    window.history.pushState(null, '', window.location.pathname);

    // Listen for popstate events (browser back/forward buttons)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isGenerationComplete, cleanupGeneration, onCancel]);

  // Handle cancel button click
  const handleCancelClick = () => {
    if (isGenerationComplete) {
      onCancel();
      return;
    }

    Modal.confirm({
      title: '確定要取消嗎？',
      content: '報告生成尚未完成，取消後將停止生成。您確定要取消嗎？',
      okText: '確定取消',
      cancelText: '繼續等待',
      okButtonProps: { danger: true },
      onOk: async () => {
        isNavigatingRef.current = true;
        await cleanupGeneration();
        onCancel();
      },
    });
  };

  return (
    <Style.Container>
      <Style.Content>
        <Style.Title>正在生成您的 SDGs 分析報告</Style.Title>

        <Style.ProgressContainer>
          <Style.ProgressBar>
            <Style.ProgressFill progress={progress} />
          </Style.ProgressBar>
          <Style.ProgressText>{Math.round(progress)}%</Style.ProgressText>
        </Style.ProgressContainer>

        <Style.StatusText>{statusText}</Style.StatusText>

        <Style.StepIndicator>
          <Style.StepText>
            {currentStep} / {TOTAL_STEPS} 項指標
          </Style.StepText>
        </Style.StepIndicator>

        <Style.InfoText>
          我們正在使用 AI 分析您的問卷內容
          <br />
          生成專業的 SDG 分析報告與回饋建議
          <br />
          {isSubmitting ? '正在提交問卷...' : '這可能需要一些時間，請稍候...'}
        </Style.InfoText>

        <Style.CancelButton onClick={handleCancelClick}>
          取消生成
        </Style.CancelButton>
      </Style.Content>
    </Style.Container>
  );
}
